import type { CityConfig } from "../city-template/types";

export const gallupConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Gallup",
  state: "New Mexico",
  stateAbbr: "NM",
  slug: "gallup",
  route: "/cities/gallup",
  title: "Emergency Passport Gallup, NM — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Gallup, NM — as fast as 24 hours. A+ BBB rated, registered State Dept. courier serving Navajo Nation residents, Route 66 travelers, UNM-Gallup students, and McKinley County families.",
  h1: "Emergency & Expedited Passport Services in Gallup, NM",
  heroSubtitle: "Navajo Nation delegation traveling to an international indigenous conference? UNM-Gallup study-abroad program starting in two weeks? Cross-border family visit to Mexico and your passport is expired? We help Gallup residents — from Navajo families to Zuni Pueblo members to Route 66 business owners — get their passports fast. As a registered U.S. Department of State courier, we offer a best price guarantee and rates 30–100% lower than FedEx, Staples, and other third-party resellers. As fast as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Panoramic%20view%20of%20Gallup%20New%20Mexico%20along%20historic%20Route%2066%20at%20golden%20sunset%20with%20the%20iconic%20El%20Rancho%20Hotel%20sign%20glowing%2C%20red%20sandstone%20cliffs%20and%20mesas%20rising%20behind%20the%20downtown%20district%2C%20vintage%20neon%20signs%20and%20Southwestern%20architecture%20along%20the%20main%20street%2C%20the%20high%20desert%20landscape%20of%20McKinley%20County%20stretching%20to%20the%20horizon%2C%20warm%20terra%20cotta%20and%20golden%20tones%2C%20professional%20travel%20photography&width=1800&height=700&seq=gallup-hero-2026&orientation=landscape",
  heroImageAlt: "Gallup New Mexico Route 66 at golden sunset with red sandstone mesas",
  geoPosition: "35.5281;-108.7426",
  geoPlacename: "Gallup, New Mexico",
  lastModified: "2026-06-07",
  regionalAgency: {
    name: "El Paso Passport Agency",
    address: "700 E San Antonio Ave, Suite 200N, El Paso, TX 79901",
    distance: "Located in downtown El Paso, TX — approximately 330 miles south of Gallup. Appointments required, typically booked 2+ weeks out. The nearest passport agency serving the region.",
  },
  nearbyCitiesZips: [
    { name: "Gallup", zips: "87301, 87305" },
    { name: "Window Rock (AZ)", zips: "86515" },
    { name: "Zuni", zips: "87327" },
    { name: "Ramah", zips: "87321" },
    { name: "Vanderwagen", zips: "87326" },
    { name: "Gamerco", zips: "87317" },
    { name: "Grants", zips: "87020" },
    { name: "Crownpoint", zips: "87313" },
    { name: "Thoreau", zips: "87323" },
    { name: "Albuquerque (closest major airport)", zips: "87101–87199" },
  ],
  benefits: [
    { icon: "ri-flashlight-line", title: "24-Hour McKinley County Emergency Processing", desc: "Reserved courier access — as fast as 24 hours for Gallup-area travelers with immediate departures. No need to drive 330 miles to El Paso or 140 miles to Albuquerque for agency appointments." },
    { icon: "ri-earth-line", title: "Navajo Nation & Tribal Community Support", desc: "We serve Navajo, Zuni, and Hopi community members with culturally aware passport services. We understand the unique documentation needs for Certificate of Indian Blood (CIB) and tribal enrollment verification." },
    { icon: "ri-flight-takeoff-line", title: "Regional Airport Connections", desc: "Gallup Municipal Airport (GUP) is limited — most international travelers use Albuquerque International Sunport (ABQ, 140 miles east) or Phoenix Sky Harbor (PHX, 260 miles west). We coordinate timing around your departure airport." },
    { icon: "ri-landscape-line", title: "Route 66 & Tourism Economy Support", desc: "Gallup's tourism economy means international visitors and business owners need reliable passport services. We handle expedited passports for Route 66 businesses, trading posts, and cultural tourism professionals." },
  ],
  internalLinks: [
    { name: "Fast passport services in Albuquerque, NM", route: "/cities/albuquerque" },
    { name: "Expedited passport in Farmington, NM", route: "/cities/farmington" },
    { name: "Urgent passport services in Flagstaff, AZ", route: "/cities/flagstaff" },
    { name: "New Mexico passport services overview", route: "/states/new-mexico" },
  ],
  fpcOfficeAddress: "700 E San Antonio Ave, Suite 200N, El Paso, TX 79901",
  fpcOfficeNote: "Fast Passport Center serves Gallup via secure registered courier with direct access to the El Paso Passport Agency. Our registered couriers hand-deliver your documents — meaning you get faster results without the appointment wait. Gallup residents can ship from any UPS Store, FedEx Office, or post office location across McKinley County.",
  fpcOfficeDistance: "Serving Gallup, Window Rock, Zuni, Ramah, Vanderwagen, Gamerco, Crownpoint, Thoreau, and all McKinley County communities",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Gallup and all of McKinley County. From the historic Route 66 downtown corridor to the north-side neighborhoods, from the UNM-Gallup campus to the Red Rock State Park area — we serve the entire Gallup region including the Navajo Nation and Zuni Pueblo. Our courier service eliminates the need to drive hours to a passport agency.",
  nearbyCitiesIntro: "We serve Gallup and surrounding McKinley County communities including Window Rock (Navajo Nation capital), Zuni Pueblo, Ramah, Crownpoint, Thoreau, and Grants (Cibola County). We also serve Navajo Nation members across the Arizona-New Mexico border region. Albuquerque International Sunport (ABQ) is 140 miles east for international departures.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Gallup as simple as possible — with the respect and understanding McKinley County's diverse communities deserve. Because we are a registered U.S. Department of State courier (not a reseller like FedEx or Staples), we pass those savings directly to you with our best price guarantee. Most Gallup travelers start online and ship documents overnight — no office visit required for most services.",
  mistakesIntro: "Gallup travelers encounter unique passport issues — everything from tribal documentation requirements to rural mail delivery timelines. Here are the most common passport mistakes we see from this area.",
  testimonialsIntro: "Gallup travelers — from Navajo Nation families to UNM-Gallup students to McKinley County business owners — trust us when their international departure is days away.",
  faqIntro: "Have questions about expedited passports in Gallup, NM? Here are the most common ones we hear from McKinley County travelers.",
  statePageRoute: "/states/new-mexico",
  statePageName: "New Mexico",
  servicesIntro: "Whether you need a renewal before a flight from Albuquerque, a first-time passport for an indigenous cultural exchange, or an emergency passport for a family trip — Fast Passport Center handles it with reserved courier submission availability. As a registered State Department courier (not a reseller), we guarantee the best price and typically cost 30–100% less than FedEx, Staples, and other third-party passport courier resellers.",
  dmvWebsite: "https://www.co.mckinley.nm.us/",
  dmvLabel: "McKinley County MVD — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Local Area", href: "#service-area" },
    { label: "Reviews", href: "#testimonials" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Compare", href: "#comparison" },
    { label: "Mistakes", href: "#mistakes" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Tribal delegation to an international conference in 3 days? Family emergency requiring immediate travel? Our emergency courier service submits directly via reserved agency slots — as fast as 24 hours for Gallup-area travelers.",
      bullets: ["As fast as 24 hours", "Reserved agency submission", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already hold a U.S. passport? We help Gallup residents renew fast — no agency visit required for most renewals. Perfect for McKinley County professionals and Navajo Nation members preparing for international travel.",
      bullets: ["Eligible for expedited processing", "Ship documents overnight", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time applicants require an in-person acceptance clerk visit. We guide Gallup residents through every step — from downtown Route 66 to the Navajo Nation — with clear instructions for the nearest acceptance location.",
      bullets: ["Step-by-step checklist provided", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost or Stolen Passport",
      description: "Replacements require additional forms. We guide you through DS-64 and everything needed to avoid costly mistakes — so your international travel or cultural exchange is not derailed.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Minors require parental participation and an in-person acceptance step. We provide clear instructions and checklists so your family trip abroad stays on schedule.",
      bullets: ["Both parents must consent", "Clear checklist provided", "Expedited options available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "After marriage, divorce, or a legal name change — we assist with corrected passports so your travel documents match your reservations and identification.",
      bullets: ["Marriage certificate guidance", "Legal name change support", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-book-2-line",
      title: "Second Passport",
      description: "Need a second passport for frequent travel? Ideal for Navajo Nation officials, cultural exchange participants, and McKinley County business owners who travel internationally while their primary passport is in visa processing.",
      bullets: ["Frequent traveler solutions", "Backup travel document", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
    {
      icon: "ri-file-damage-line",
      title: "Damaged Passport",
      description: "Replacing a significantly damaged passport is faster than you think. We guide Gallup-area travelers through the process so your international itinerary stays on track.",
      bullets: ["Damaged passport replacement", "Document review included", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=damaged-passport",
    },
    {
      icon: "ri-id-card-line",
      title: "Passport Card",
      description: "The passport card is a wallet-size travel document for land and sea crossings to Canada, Mexico, the Caribbean, and Bermuda. Convenient for McKinley County residents who visit border communities.",
      bullets: ["Wallet-size convenience", "Land and sea travel to nearby destinations", "Same expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-card",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Gallup NM", description: "Urgent passport processing for Gallup New Mexico travelers with immediate departure needs from Albuquerque Sunport." },
    { name: "Expedited Passport Renewal Gallup NM", description: "Fast passport renewal for Gallup New Mexico residents with a previously-issued passport." },
    { name: "First-Time Passport Gallup NM", description: "New passport application services for Gallup New Mexico residents applying for their first passport." },
    { name: "Child Passport Gallup NM", description: "Minor passport services for Gallup New Mexico families with children under 16." },
    { name: "Name Change Passport Gallup NM", description: "Passport name correction after marriage, divorce, or legal name changes for Gallup residents." },
    { name: "Second Passport Gallup NM", description: "Second passport services for Navajo Nation officials and frequent international travelers." },
  ],
  testimonials: [
    {
      name: "Leonard Y.",
      location: "Gallup (Navajo Nation)",
      rating: 5,
      text: "I was selected for an indigenous cultural exchange in New Zealand with 10 days notice. My passport had expired and I had no idea where to start. Fast Passport Center walked me through everything and got my passport back in 4 days. They understood the tribal documentation requirements perfectly.",
      service: "Emergency Passport Renewal",
    },
    {
      name: "The Begay Family",
      location: "Window Rock",
      rating: 5,
      text: "We planned a family trip to visit relatives in Canada and realized three of our family members needed new passports. They handled all of us efficiently and caught a photo issue before submission. Passports arrived in 6 days. Excellent service for Navajo families.",
      service: "Family Passport Services",
    },
    {
      name: "Maria C.",
      location: "Gallup",
      rating: 5,
      text: "I own a trading post on Route 66 and travel to Santa Fe Indian Market and international art shows. My passport renewal was urgent and they got it back in 5 days. The remote process was perfect — I never had to close my shop to drive to Albuquerque.",
      service: "Passport Renewal",
    },
    {
      name: "Tyler J.",
      location: "Zuni",
      rating: 5,
      text: "UNM-Gallup student heading to a semester abroad in Costa Rica. First passport ever. They provided a clear checklist and guided me through the Zuni Post Office acceptance process. Everything went smoothly and my passport arrived in 8 days.",
      service: "New Passport",
    },
  ],
  facilities: [
    {
      id: "gal-1",
      name: "Gallup Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "500 S 2nd St",
      city: "Gallup",
      state: "NM",
      zip: "87301",
      phone: "(505) 722-6211",
      hours: "Mon–Fri 9:00 AM – 4:00 PM (appointment required)",
      distance: "Downtown Gallup",
      website: "https://tools.usps.com/find-location.htm?location=1376538",
      websiteLabel: "USPS Location",
      notes: "Main passport acceptance facility in Gallup. Photo services on-site. Serves downtown Gallup and the Route 66 corridor. Appointment strongly recommended — walk-in availability is extremely limited in this high-demand rural facility.",
      icon: "ri-mail-line",
    },
    {
      id: "gal-2",
      name: "McKinley County Clerk — Gallup",
      type: "County Clerk Passport Acceptance",
      address: "207 W Hill Ave, Room 100",
      city: "Gallup",
      state: "NM",
      zip: "87301",
      phone: "(505) 863-6866",
      hours: "Mon–Fri 8:00 AM – 4:30 PM",
      distance: "Gallup — McKinley County Courthouse",
      website: "https://www.co.mckinley.nm.us/",
      websiteLabel: "McKinley County Clerk",
      notes: "Official county clerk passport acceptance facility. Serves all of McKinley County. No photo services on-site — bring completed passport photos. Appointment recommended. Convenient downtown location.",
      icon: "ri-government-line",
    },
    {
      id: "gal-3",
      name: "Window Rock Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "100 Window Rock Blvd",
      city: "Window Rock",
      state: "AZ",
      zip: "86515",
      phone: "(928) 871-6324",
      hours: "Mon–Fri 9:00 AM – 3:30 PM",
      distance: "Window Rock — Navajo Nation Capital",
      website: "https://tools.usps.com/find-location.htm?location=1377586",
      websiteLabel: "USPS Location",
      notes: "Serves Window Rock and the Navajo Nation capital area. Limited hours — call ahead to confirm passport services. Convenient for Navajo Nation residents near the Arizona border. Photo services may not be available.",
      icon: "ri-mail-line",
    },
    {
      id: "gal-4",
      name: "Zuni Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1200 State Highway 53",
      city: "Zuni",
      state: "NM",
      zip: "87327",
      phone: "(505) 782-4511",
      hours: "Mon–Fri 9:00 AM – 3:00 PM",
      distance: "Zuni Pueblo — southern McKinley County",
      website: "https://tools.usps.com/find-location.htm?location=1377579",
      websiteLabel: "USPS Location",
      notes: "Serves Zuni Pueblo and surrounding communities. Smaller facility with very limited hours — call well ahead to confirm passport availability. Essential for Zuni residents who cannot easily travel to Gallup.",
      icon: "ri-mail-line",
    },
    {
      id: "gal-5",
      name: "Grants Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "511 W Santa Fe Ave",
      city: "Grants",
      state: "NM",
      zip: "87020",
      phone: "(505) 287-0526",
      hours: "Mon–Fri 9:00 AM – 4:00 PM",
      distance: "Grants — Cibola County (60 miles east)",
      website: "https://tools.usps.com/find-location.htm?location=1377558",
      websiteLabel: "USPS Location",
      notes: "Alternative facility for eastern McKinley County and Cibola County residents. Photo services available. Less busy than Gallup — may have shorter wait times.",
      icon: "ri-mail-line",
    },
    {
      id: "gal-6",
      name: "Albuquerque Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1135 Broadway Blvd NE",
      city: "Albuquerque",
      state: "NM",
      zip: "87101",
      phone: "(505) 346-6937",
      hours: "Mon–Fri 9:00 AM – 4:00 PM (appointment required)",
      distance: "Albuquerque — 140 miles east",
      website: "https://tools.usps.com/find-location.htm?location=1376531",
      websiteLabel: "USPS Location",
      notes: "Largest facility in New Mexico. Full photo services. Convenient for Gallup residents heading to ABQ Sunport — can stop for acceptance on the way to the airport. More appointment availability than rural locations.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — typically booked 2+ weeks out, walk-ins not accepted", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Required — drive to El Paso Passport Agency (330 miles) or Denver (480 miles)", fpc: "Most Gallup travelers ship documents overnight" },
    { feature: "Processing Speed", agency: "Life-or-death emergencies only — strict eligibility with proof of travel", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection and delay", fpc: "Professional review to reduce processing delays" },
    { feature: "Courier Access", agency: "Not available to the public — agency serves emergencies only", fpc: "Reserved daily submission availability as registered courier" },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable — no guarantee", fpc: "Predictable processing based on selected speed option" },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel only with proof of departure date", fpc: "Most U.S. citizens with required documentation" },
    { feature: "Cost vs Resellers", agency: "Government fees only — but you handle all shipping and logistics", fpc: "Best price guarantee — 30-100% less than FedEx, Staples & resellers" },
    { feature: "Rural NM Guidance", agency: "Generic — no McKinley County, tribal, or rural expertise", fpc: "Full McKinley County/Navajo Nation/rural NM knowledge built in" },
  ],
  comparisonExtraColumns: undefined,
  mistakes: [
    {
      icon: "ri-error-warning-line",
      title: "Assuming You Can Get a Passport Without a Birth Certificate",
      description: "Some Navajo Nation elders were born at home without formal birth registration. The State Department has specific procedures for delayed birth certificates. We guide you through the correct documentation path so your application is not rejected.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Tribal ID Confusion at Acceptance Facilities",
      description: "Not all acceptance clerks are familiar with tribal identification documents. We provide guidance on which forms of ID are accepted and how to present tribal enrollment documentation alongside your passport application.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Rural Mail Delivery Delays Affecting Timelines",
      description: "McKinley County includes large rural areas where mail delivery can add 2-4 extra days each direction. Standard passport processing timelines assume urban delivery — we build rural delivery buffer into our schedule estimates.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Missing Parental Consent for Minors in Multi-Generational Households",
      description: "In cases where grandparents or extended family are primary caregivers, the parental consent requirements (Form DS-3053) need careful attention. We help ensure the correct guardian signatures are in place.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Photo Standards at Rural Photo Providers",
      description: "Not all photo providers in the Gallup area consistently produce State Department-compliant photos. We can advise on which locations reliably meet the white background, no glasses, neutral expression requirements.",
    },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose the processing speed that fits your travel date and complete your secure order. Takes just a few minutes from your home, chapter house, or local library." },
    { number: "02", icon: "ri-file-list-3-line", title: "We Confirm & Send Your Checklist", description: "We confirm your details and provide a personalized checklist, instructions, and document review guidance tailored to your specific situation and documentation needs." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Your Documents Overnight", description: "Send your documents using an overnight shipping option. Most Gallup clients ship from the Main Post Office on 2nd Street or use UPS/FedEx drop boxes." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit through our reserved passport agency availability — so you do not have to drive hours to El Paso, Denver, or any other passport agency." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport Is Completed", description: "Your passport is completed within the timeframe you selected. No long-distance driving, no agency appointment anxiety — just a passport delivered to your Gallup address." },
  ],
  faqs: [
    { q: "How fast can I get a passport in Gallup?", a: "We offer Gallup-area clients expedited processing as fast as 24 hours. Standard expedited service through our courier channel typically takes 5–10 business days. We review your documents before submission to catch errors that cause delays." },
    { q: "Do I need to visit an office in Gallup?", a: "No. Our service is remote — you complete forms online, visit a local acceptance facility (the Gallup Post Office or McKinley County Clerk), then ship documents overnight to our processing center. We handle the State Department submission and return your passport to your Gallup address." },
    { q: "Where can I get passport acceptance in Gallup?", a: "Gallup has acceptance at the Main Post Office (500 S 2nd St) and the McKinley County Clerk (207 W Hill Ave). Window Rock, Zuni, and Grants also have facilities. We maintain an updated list with hours, photo availability, and appointment options." },
    { q: "What is the closest passport agency to Gallup?", a: "The closest passport agency is the El Paso Passport Agency at 700 E San Antonio Ave, El Paso, TX — approximately 330 miles south. The Denver Passport Agency is about 480 miles north. Neither is practical for a day trip. Our courier channel eliminates the need to travel to any agency." },
    { q: "Can I use tribal identification for my passport application?", a: "Tribal enrollment cards and Certificates of Indian Blood (CIB) may be used as secondary identification but typically cannot serve as primary photo ID. A state-issued driver's license, state ID card, or other federally recognized photo ID is required. We can advise on your specific documentation situation." },
    { q: "What documents do Gallup applicants need?", a: "New passport: certified birth certificate, government-issued photo ID, DS-11 form, and passport photo. Renewal: DS-82 form, current passport, and passport photo. If you have a delayed or amended birth certificate, we can guide you through the additional documentation requirements." },
    { q: "Can you help if I was born on the reservation without a hospital birth certificate?", a: "Yes. The State Department has procedures for delayed birth certificates and alternative birth documentation. We can guide you through the acceptable forms of birth evidence and help you gather the right documents before submission." },
    { q: "How do I get a passport photo in Gallup?", a: "The Gallup Main Post Office offers photo services on-site. Walgreens (910 US-491) and CVS (700 US-491) also offer passport photos. We recommend confirming photo standards compliance before visiting. Photos must be 2x2 inches, white background, no glasses, neutral expression." },
    { q: "How do I check my passport status?", a: "Once submitted, we provide tracking at every stage. You can also check the State Department's online passport status system at travel.state.gov. Processing from Gallup to final return shipping typically matches your chosen speed tier." },
    { q: "Can you serve Navajo Nation residents living in remote areas?", a: "Yes. We serve all McKinley County and Navajo Nation residents regardless of location. If you live in a remote area with limited mail service, we can advise on the best shipping options — including using the Window Rock or Crownpoint post offices as your acceptance and shipping point." },
  ],
  photoLocations: [
    { name: "Gallup Main Post Office Photos", address: "500 S 2nd St, Gallup, NM 87301", hours: "Mon–Fri 9 AM – 4 PM", website: "https://www.usps.com/" },
    { name: "Walgreens — Gallup", address: "910 US-491, Gallup, NM 87301", hours: "Daily 8 AM – 10 PM", website: "https://www.walgreens.com/" },
    { name: "CVS Pharmacy — Gallup", address: "700 US-491, Gallup, NM 87301", hours: "Daily 8 AM – 9 PM", website: "https://www.cvs.com/" },
    { name: "Walmart — Gallup", address: "1650 W Maloney Ave, Gallup, NM 87301", hours: "Daily 6 AM – 11 PM", website: "https://www.walmart.com/" },
  ],
  dmvLocations: [
    {
      name: "McKinley County MVD — Gallup",
      address: "207 W Hill Ave, Gallup, NM 87301",
      hours: "Mon–Fri, 8:00 AM – 4:00 PM",
      note: "Driver licenses, REAL ID, vehicle registration. County courthouse location. Appointment recommended.",
      featured: true,
    },
    {
      name: "New Mexico MVD — Gallup",
      address: "1720 E Aztec Ave, Gallup, NM 87301",
      hours: "Mon–Fri, 8:00 AM – 4:30 PM",
      note: "Full-service MVD office. REAL ID available. Appointment strongly recommended.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "GUP",
      name: "Gallup Municipal Airport",
      distance: "About 2 miles east of downtown Gallup",
      address: "3401 W Highway 66, Gallup, NM 87301",
      note: "General aviation airport with no scheduled commercial service. Most travelers use ABQ (Albuquerque) or PHX (Phoenix) for scheduled departures.",
      flightUrl: "https://www.gallupnm.gov/160/Airport",
      flightLabel: "GUP Airport Website",
      featured: true,
    },
    {
      code: "ABQ",
      name: "Albuquerque International Sunport",
      distance: "About 140 miles east of Gallup",
      address: "2200 Sunport Blvd, Albuquerque, NM 87106",
      note: "New Mexico's largest airport. Direct international flights to Mexico and extensive domestic connections for international gateways via DFW, LAX, DEN, and PHX. Primary airport for Gallup international travelers.",
      flightUrl: "https://www.abqsunport.com/",
      flightLabel: "ABQ Airport Website",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    {
      icon: "ri-user-smile-line",
      label: "Birth Certificate",
      desc: "Request certified birth certificates from the New Mexico Department of Health. For delayed birth certificates or birth records from Navajo Nation areas, contact the NM Bureau of Vital Records directly.",
      url: "https://www.nmhealth.org/about/erd/bvrhs/vrp/",
    },
    {
      icon: "ri-file-list-2-line",
      label: "Death Certificate",
      desc: "Obtain certified death certificates through the NM Department of Health Vital Records. Required for estate and legal matters in McKinley County.",
      url: "https://www.nmhealth.org/about/erd/bvrhs/vrp/",
    },
    {
      icon: "ri-hearts-line",
      label: "Marriage Certificate",
      desc: "Request marriage license records from the McKinley County Clerk. Certified copies available for name changes and legal purposes.",
      url: "https://www.co.mckinley.nm.us/",
    },
    {
      icon: "ri-file-copy-line",
      label: "Divorce Decree",
      desc: "Obtain certified divorce records through the Eleventh Judicial District Court. Required for name change passport corrections.",
      url: "https://eleventhdistrictcourt.nmcourts.gov/",
    },
  ],
};